import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  PrinterOutlined,
  SaveOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import cspmService from '@/services/cspm.service';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useCspmPermissions } from '@/hooks/useCspmPermissions';
import { ItemSelect, formatCatalogItemDescription, type ItemLike } from '@/components/inventory/ItemSelect';
import { CspmSourceDocumentSelect } from '@/components/cspm/CspmSourceDocumentSelect';
import { isCspmCreateRoute } from '@/utils/cspmCreateRoute';
import {
  CSPM_CLASSIFICATIONS,
  CSPM_CLASSIFICATION_LABELS,
  CSPM_CRITICALITY,
  CSPM_CRITICALITY_LABELS,
  CSPM_PURPOSES,
  CSPM_PURPOSE_LABELS,
  CSPM_SOURCE_TYPES,
  CSPM_SOURCE_TYPE_LABELS,
  enumOptions,
  type CustomerPropertyRequirementLine,
} from '@/constants/cspm';
import { EngineCspmRequirementPrint } from '@/components/print/EngineLetterheadPrints';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_CUSTOMER_PROPERTY_REQUIREMENT } from '@/constants/document-entity-types';

const { Text } = Typography;

type LineRow = CustomerPropertyRequirementLine & { key: string };

const emptyLine = (n: number): LineRow => ({
  key: `new-${n}-${Date.now()}`,
  description: '',
  expected_qty: 1,
  uom: 'PCS',
  classification: 'OTHER',
  purpose: 'FOR_CONSUMPTION',
  criticality: 'NON_CRITICAL',
});

const STATUS_COLOR: Record<string, string> = {
  Draft: 'default',
  Open: 'processing',
  Closed: 'cyan',
  Cancelled: 'error',
};

const CustomerPropertyRequirementPage: React.FC = () => {
  const { id: routeId } = useParams<{ id: string }>();
  const location = useLocation();
  const isNew = isCspmCreateRoute(routeId, location.pathname);
  const id = isNew ? undefined : routeId;
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const cspmPerms = useCspmPermissions();
  const [form] = Form.useForm();
  const [lines, setLines] = useState<LineRow[]>([emptyLine(1)]);
  const [printOpen, setPrintOpen] = useState(false);

  const { data: customers = [] } = useActiveCustomers();
  const customerOptions = useMemo(
    () =>
      (customers as Array<{ id: string; customer_name?: string; name?: string }>).map((c) => ({
        value: c.id,
        label: c.customer_name || c.name || c.id,
      })),
    [customers]
  );

  const { data: requirement, isLoading } = useQuery({
    queryKey: ['cspm', 'requirement', id],
    queryFn: () => cspmService.getRequirement(id!),
    enabled: !isNew && Boolean(id),
  });

  const isDraft = isNew || requirement?.status === 'Draft';
  const watchedCustomerId = Form.useWatch('customer_id', form);
  const watchedSourceType = Form.useWatch('source_document_type', form);
  const customerName =
    customerOptions.find((c) => c.value === (requirement?.customer_id || watchedCustomerId))
      ?.label || requirement?.customer_name;

  useEffect(() => {
    if (!requirement) return;
    form.setFieldsValue({
      customer_id: requirement.customer_id,
      source_document_type: requirement.source_document_type,
      source_document_id: requirement.source_document_id,
      remarks: requirement.remarks,
    });
    setLines(
      (requirement.lines || []).map((ln, idx) => ({
        ...ln,
        key: ln.id || `line-${idx}`,
      }))
    );
  }, [requirement, form]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['cspm'] });
  };

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => cspmService.createRequirement(body),
    onSuccess: (row) => {
      message.success('Requirement created');
      invalidate();
      if (row?.id) navigate(`/cspm/requirements/${row.id}`, { replace: true });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Create failed'),
  });

  const updateMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => cspmService.updateRequirement(id!, body),
    onSuccess: () => {
      message.success('Saved');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  const actionMut = useMutation({
    mutationFn: (action: 'open' | 'close' | 'cancel') => {
      if (action === 'open') return cspmService.openRequirement(id!);
      if (action === 'close') return cspmService.closeRequirement(id!);
      return cspmService.cancelRequirement(id!);
    },
    onSuccess: (_r, action) => {
      message.success(
        action === 'open' ? 'Opened' : action === 'close' ? 'Closed' : 'Cancelled'
      );
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Action failed'),
  });

  const buildBody = async () => {
    const values = await form.validateFields();
    const payloadLines = lines
      .map((ln) => ({
        classification: ln.classification,
        purpose: ln.purpose,
        criticality: ln.criticality,
        item_id: ln.item_id || null,
        customer_item_ref: ln.customer_item_ref || null,
        description: String(ln.description || '').trim(),
        expected_qty: Number(ln.expected_qty) || 0,
        uom: ln.uom || 'PCS',
        remarks: ln.remarks || null,
      }))
      .filter((ln) => ln.description);
    if (!payloadLines.length) {
      throw new Error('At least one line with a description is required');
    }
    return { ...values, lines: payloadLines };
  };

  const onSave = async () => {
    try {
      const body = await buildBody();
      if (isNew) createMut.mutate(body);
      else updateMut.mutate(body);
    } catch (e: any) {
      message.error(e?.message || 'Please complete required fields');
    }
  };

  const updateLine = (key: string, patch: Partial<LineRow>) => {
    setLines((prev) => prev.map((ln) => (ln.key === key ? { ...ln, ...patch } : ln)));
  };

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/cspm')}>
                Back
              </Button>
              <div>
                <h2 style={{ margin: 0 }}>
                  {isNew
                    ? 'New requirement'
                    : requirement?.requirement_number || 'Externally owned property requirement'}
                </h2>
                {!isNew && requirement?.status && (
                  <Tag color={STATUS_COLOR[requirement.status] || 'default'}>{requirement.status}</Tag>
                )}
              </div>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              {!isNew && (
                <Button icon={<PrinterOutlined />} onClick={() => setPrintOpen(true)}>
                  Print
                </Button>
              )}
              {isDraft &&
                ((isNew && cspmPerms.canRequirementCreate) ||
                  (!isNew && cspmPerms.canRequirementEdit)) && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={createMut.isPending || updateMut.isPending}
                  onClick={onSave}
                >
                  Save
                </Button>
              )}
              {!isNew && requirement?.status === 'Draft' && cspmPerms.canRequirementEdit && (
                <Button
                  icon={<CheckOutlined />}
                  loading={actionMut.isPending}
                  onClick={() => actionMut.mutate('open')}
                >
                  Open
                </Button>
              )}
              {!isNew && requirement?.status === 'Open' && cspmPerms.canRequirementEdit && (
                <Button
                  icon={<CloseOutlined />}
                  loading={actionMut.isPending}
                  onClick={() => actionMut.mutate('close')}
                >
                  Close
                </Button>
              )}
              {!isNew &&
                requirement &&
                !['Cancelled', 'Closed'].includes(requirement.status) &&
                cspmPerms.canRequirementEdit && (
                <Button
                  danger
                  icon={<StopOutlined />}
                  loading={actionMut.isPending}
                  onClick={() =>
                    modal.confirm({
                      title: 'Cancel this requirement?',
                      onOk: () => actionMut.mutateAsync('cancel'),
                    })
                  }
                >
                  Cancel
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        <Card title="Header">
          <Form form={form} layout="vertical" disabled={!isDraft}>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="customer_id"
                  label="Customer"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={customerOptions}
                    placeholder="Select customer"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="source_document_type"
                  label="Source type"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Select
                    options={enumOptions(CSPM_SOURCE_TYPES, CSPM_SOURCE_TYPE_LABELS)}
                    onChange={() => form.setFieldsValue({ source_document_id: undefined })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="source_document_id"
                  label="Source document"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <CspmSourceDocumentSelect
                    sourceType={watchedSourceType}
                    customerId={watchedCustomerId}
                    onValidated={(result) => {
                      if (result?.customer_id) {
                        form.setFieldsValue({ customer_id: result.customer_id });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="remarks" label="Remarks">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <Card
          title="Lines"
          extra={
            isDraft ? (
              <Button
                icon={<PlusOutlined />}
                onClick={() => setLines((prev) => [...prev, emptyLine(prev.length + 1)])}
              >
                Add line
              </Button>
            ) : null
          }
        >
          <Table
            rowKey="key"
            dataSource={lines}
            pagination={false}
            scroll={{ x: 1300 }}
            columns={[
              {
                title: '#',
                width: 50,
                render: (_: unknown, __: LineRow, idx: number) => idx + 1,
              },
              {
                title: 'Item',
                width: 220,
                render: (_: unknown, row: LineRow) =>
                  isDraft ? (
                    <ItemSelect
                      value={row.item_id || undefined}
                      allowClear
                      style={{ width: '100%' }}
                      placeholder="Optional inventory item"
                      onChange={(itemId, option) => {
                        const fromOpt = (option as { item?: ItemLike } | undefined)?.item;
                        updateLine(row.key, {
                          item_id: itemId || null,
                          description:
                            row.description?.trim() ||
                            (fromOpt ? formatCatalogItemDescription(fromOpt) : row.description),
                          uom: fromOpt?.base_uom || row.uom || 'PCS',
                        });
                      }}
                    />
                  ) : (
                    row.item_id || '—'
                  ),
              },
              {
                title: 'Description',
                dataIndex: 'description',
                render: (v: string, row: LineRow) =>
                  isDraft ? (
                    <Input
                      value={v}
                      onChange={(e) => updateLine(row.key, { description: e.target.value })}
                    />
                  ) : (
                    v || '—'
                  ),
              },
              {
                title: 'Class',
                width: 150,
                render: (_: unknown, row: LineRow) =>
                  isDraft ? (
                    <Select
                      style={{ width: '100%' }}
                      value={row.classification}
                      options={enumOptions(CSPM_CLASSIFICATIONS, CSPM_CLASSIFICATION_LABELS)}
                      onChange={(classification) => updateLine(row.key, { classification })}
                    />
                  ) : (
                    CSPM_CLASSIFICATION_LABELS[row.classification as keyof typeof CSPM_CLASSIFICATION_LABELS] ||
                    row.classification
                  ),
              },
              {
                title: 'Purpose',
                width: 160,
                render: (_: unknown, row: LineRow) =>
                  isDraft ? (
                    <Select
                      style={{ width: '100%' }}
                      value={row.purpose}
                      options={enumOptions(CSPM_PURPOSES, CSPM_PURPOSE_LABELS)}
                      onChange={(purpose) => updateLine(row.key, { purpose })}
                    />
                  ) : (
                    CSPM_PURPOSE_LABELS[row.purpose as keyof typeof CSPM_PURPOSE_LABELS] || row.purpose
                  ),
              },
              {
                title: 'Criticality',
                width: 130,
                render: (_: unknown, row: LineRow) =>
                  isDraft ? (
                    <Select
                      style={{ width: '100%' }}
                      value={row.criticality}
                      options={enumOptions(CSPM_CRITICALITY, CSPM_CRITICALITY_LABELS)}
                      onChange={(criticality) => updateLine(row.key, { criticality })}
                    />
                  ) : (
                    CSPM_CRITICALITY_LABELS[row.criticality as keyof typeof CSPM_CRITICALITY_LABELS] ||
                    row.criticality
                  ),
              },
              {
                title: 'Expected qty',
                width: 120,
                render: (_: unknown, row: LineRow) =>
                  isDraft ? (
                    <InputNumber
                      min={0}
                      style={{ width: '100%' }}
                      value={row.expected_qty}
                      onChange={(expected_qty) =>
                        updateLine(row.key, { expected_qty: Number(expected_qty) || 0 })
                      }
                    />
                  ) : (
                    row.expected_qty
                  ),
              },
              {
                title: 'UOM',
                width: 90,
                render: (_: unknown, row: LineRow) =>
                  isDraft ? (
                    <Input
                      value={row.uom}
                      onChange={(e) => updateLine(row.key, { uom: e.target.value })}
                    />
                  ) : (
                    row.uom
                  ),
              },
              {
                title: 'Received',
                width: 90,
                render: (_: unknown, row: LineRow) => row.received_qty ?? 0,
              },
              ...(isDraft
                ? [
                    {
                      title: '',
                      width: 48,
                      render: (_: unknown, row: LineRow) => (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          disabled={lines.length <= 1}
                          onClick={() => setLines((prev) => prev.filter((l) => l.key !== row.key))}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />
          {!isDraft && (
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Header and lines are read-only after open.
            </Text>
          )}
        </Card>

        <EntityAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_CUSTOMER_PROPERTY_REQUIREMENT}
          entityId={isNew ? null : id}
          title="Attachments"
          readOnly={!isDraft}
          saveFirstMessage="Save the requirement draft before attaching files."
        />
      </Space>

      <Modal
        open={printOpen}
        onCancel={() => setPrintOpen(false)}
        footer={null}
        width="90vw"
        style={{ top: 24 }}
        destroyOnClose
      >
        {requirement && (
          <EngineCspmRequirementPrint
            data={requirement}
            customerName={customerName}
            onClose={() => setPrintOpen(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default CustomerPropertyRequirementPage;
